import type { Uuid } from "spacetimedb";
import { useRutterLink } from "react-rutter-link";

import { toaster } from "@/web/components/primitives/toast";
import { env } from "@/web/lib/env";
import { ErrorHandler } from "@/web/lib/errors";
import { useProcedureMutation } from "@/web/lib/tanstack";
import { procedures } from "@tycho/web-bindings";
import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";
import { usePostHog } from "@posthog/react";
import { PosthogEvents } from "@/web/lib/posthog-events";

type ConnectQuickbooksProps = {
  /** Underwritable entity that will own the resulting rutter item. */
  underwritableEntity: Uuid;
};

function ConnectQuickbooksAction({
  underwritableEntity,
  onSkip,
  onComplete,
}: ConnectQuickbooksProps & SmbActionCoreProps) {
  const posthog = usePostHog();
  const connectAccountingMutation = useProcedureMutation(procedures.connectAccounting, {
    onSuccess: () => {
      PosthogEvents.QuickbooksConnected(posthog, underwritableEntity);
      toaster({ message: "QuickBooks connected", variant: "success" });
      onComplete();
    },
    onError: ErrorHandler.default,
  });

  const { open: openRutter, ready } = useRutterLink({
    publicKey: env.VITE_RUTTER_PUBLIC_KEY,
    onSuccess: (publicToken: string) => {
      connectAccountingMutation.mutate({
        underwritableEntity,
        rutterPublicToken: publicToken,
      });
    },
  });

  const isPending = connectAccountingMutation.isPending;

  return (
    <ActionCard category="budget">
      <ActionCard.Header>
        <ActionCard.Badge>Budget</ActionCard.Badge>
      </ActionCard.Header>
      <ActionCard.Main>
        <ActionCard.Title>Connect Quickbooks</ActionCard.Title>
        <ActionCard.Description>
          Link QuickBooks so we can verify revenue and expense history automatically.
        </ActionCard.Description>
      </ActionCard.Main>
      <ActionCard.Footer>
        <ActionCard.SkipButton onSkip={onSkip} />
        <ActionCard.PrimaryButton
          disabled={!ready || isPending}
          onClick={() => openRutter({ platform: "QUICKBOOKS" })}
        >
          {isPending ? "Connecting…" : "Connect"}
        </ActionCard.PrimaryButton>
      </ActionCard.Footer>
    </ActionCard>
  );
}

export function createConnectQuickbooksAction(
  props: ConnectQuickbooksProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = "connect-quickbooks", canSkip = true, ...rest } = props;
  return {
    id,
    category: "budget",
    canSkip,
    render: (core) => <ConnectQuickbooksAction {...rest} {...core} />,
  };
}

// ── Engine rules ─────────────────────────────────────────────────────────────
import type { AccountingConnection } from "@tycho/web-bindings/types";
import { CardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";
import { createConnectQuickbooksCompleted } from "@/web/routes/_protected/smb/-dashboard/-completedActions/ConnectQuickbooksCompleted";

export const connectQuickbooksRule = CardRulesEngine.singletonRule({
  id: "connect-quickbooks",
  priority: 75,
  when: CardRulesEngine.needsMoreQuickbooksConnections,
  build: (d) => createConnectQuickbooksAction({ underwritableEntity: d.underwritableEntityId }),
});

export const connectQuickbooksCompletedRule =
  CardRulesEngine.forEachCompletedRule<AccountingConnection>({
    id: "connect-quickbooks:done",
    forEach: CardRulesEngine.eachConnection("accounting"),
    when: CardRulesEngine.always,
    build: (_, connection) => createConnectQuickbooksCompleted({ connection }),
  });
